import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getStoreCopyJobsApi,
  retryStoreCopyJobApi,
  startStoreCopyJobApi,
} from "../api/storeCopyJobsApi";
import type { StoreCopyJobId, StoreId } from "@KassaroAB/shared/types/ids";
import type { RestStartStoreCopy } from "@KassaroAB/shared/types/storeCopyJob";
import { isActiveJobStatus } from "@KassaroAB/shared/utils/jobStatus";

const jobsQueryKey = (targetStoreId: StoreId | undefined) =>
  ["admin", "stores", targetStoreId, "copyJobs"] as const;

export const useGetStoreCopyJobsQuery = (targetStoreId: StoreId | undefined) => {
  return useQuery({
    queryKey: jobsQueryKey(targetStoreId),
    queryFn: () => getStoreCopyJobsApi(targetStoreId!),
    enabled: !!targetStoreId,
    refetchInterval: (query) => {
      const latestJob = query.state.data?.[0];
      if (latestJob && isActiveJobStatus(latestJob.status)) {
        return 2000;
      }
      return false;
    },
  });
};

export const useStartStoreCopyJobMutation = (
  targetStoreId: StoreId | undefined,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RestStartStoreCopy) =>
      startStoreCopyJobApi(targetStoreId!, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsQueryKey(targetStoreId) });
    },
  });
};

export const useRetryStoreCopyJobMutation = (
  targetStoreId: StoreId | undefined,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storeCopyJobId: StoreCopyJobId) =>
      retryStoreCopyJobApi(targetStoreId!, storeCopyJobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsQueryKey(targetStoreId) });
    },
  });
};
